<template>
  <div class="finance-graphs">
    <!-- Remove the subsidiary selector since we're using the parent's selection -->
    <!-- <div class="graphs-description">
      <p>
        <i class="fa-solid fa-chart-line"></i>
        View detailed trends for each financial metric. Graphs show monthly performance and help identify patterns over time.
      </p>
    </div> -->

    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <p>Loading graphs...</p>
      </div>
    </div>
    <div class="graphs-grid" v-show="!isLoading">
      <div class="graph-container">
        <div class="graph-header">
          <h2>Revenue Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly revenue trends over time
              <div class="tooltip-formula">Revenue = Sum of all income sources</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="revenueChartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="graph-container">
        <div class="graph-header">
          <h2>Costs Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly direct costs trends
              <div class="tooltip-formula">Costs = Sum of all direct operational costs</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="costsChartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="graph-container">
        <div class="graph-header">
          <h2>Expenses Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly overhead and operational expenses
              <div class="tooltip-formula">Expenses = Sum of all overhead costs</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="expensesChartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="graph-container">
        <div class="graph-header">
          <h2>EBITDA Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly EBITDA performance
              <div class="tooltip-formula">EBITDA = Revenue - Costs - Expenses</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="ebitdaChartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="graph-container">
        <div class="graph-header">
          <h2>Profit Before Tax Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly profit before tax performance
              <div class="tooltip-formula">Profit Before Tax = Revenue - EBITDA</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="profitBeforeTaxChartData"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="graph-container">
        <div class="graph-header">
          <h2>Gross Profit Trend</h2>
          <div class="info-tooltip">
            <i class="fa-solid fa-circle-info"></i>
            <div class="tooltip-content">
              Monthly gross profit performance
              <div class="tooltip-formula">Gross Profit = Revenue - Costs</div>
            </div>
          </div>
        </div>
        <div class="graph">
          <Bar
            :data="grossProfitChartData"
            :options="chartOptions"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import axios from 'axios'
import { ElMessage } from 'element-plus'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const props = defineProps({
  subsidiaries: {
    type: Array,
    required: true,
    default: () => []
  },
  selectedSubsidiary: {
    type: String,
    required: true
  },
  selectedYear: {
    type: Number,
    default: () => new Date().getFullYear()
  }
})

const monthlyData = ref([])
const isLoading = ref(false)
const showSubsidiaryDropdown = ref(false)
const dropdownRef = ref(null)

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return new Intl.NumberFormat('en-MY', {
            style: 'currency',
            currency: 'MYR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value)
        }
      }
    }
  }
}

const revenueChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'Revenue',
      data: monthlyData.value.map(item => item.total_revenue),
      backgroundColor: '#0ea5e9',
      borderRadius: 4
    }
  ]
}))

const costsChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'Costs',
      data: monthlyData.value.map(item => item.total_costs),
      backgroundColor: '#f97316',
      borderRadius: 4
    }
  ]
}))

const expensesChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'Expenses',
      data: monthlyData.value.map(item => item.total_expenses),
      backgroundColor: '#ef4444',
      borderRadius: 4
    }
  ]
}))

const ebitdaChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'EBITDA',
      data: monthlyData.value.map(item => item.ebitda),
      backgroundColor: '#16a34a',
      borderRadius: 4
    }
  ]
}))

const profitBeforeTaxChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'Profit Before Tax',
      data: monthlyData.value.map(item => item.profit_before_tax),
      backgroundColor: '#7c3aed',
      borderRadius: 4
    }
  ]
}))

const grossProfitChartData = computed(() => ({
  labels: monthlyData.value.map(item => item.month),
  datasets: [
    {
      label: 'Gross Profit',
      data: monthlyData.value.map(item => item.gross_profit),
      backgroundColor: '#8b5cf6',
      borderRadius: 4
    }
  ]
}))

const selectedSubsidiaryName = computed(() => {
  const selected = props.subsidiaries.find(sub => sub.id === props.selectedSubsidiary)
  return selected ? selected.name : 'Select Subsidiary'
})

// Update click outside handler
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showSubsidiaryDropdown.value = false
  }
}

const fetchGraphData = async () => {
  try {
    isLoading.value = true

    // Get the subsidiary's reporting basis
    const subsidiaryResponse = await api.get(`/api/subsidiary/${props.selectedSubsidiary}`)
    const reportingBasis = subsidiaryResponse.data.reporting_basis

    // Fetch data for all 12 months of the selected year
    const selectedYear = props.selectedYear || new Date().getFullYear()
    const fetchedData = []
    
    for (let month = 1; month <= 12; month++) {
      const monthYear = `${selectedYear}${String(month).padStart(2, '0')}`
      
      try {
        // Get revenue data from dashboard endpoint (includes adjusted revenue)
        const revenueResponse = await api.get('/api/finance/dashboard', {
          params: {
            month: monthYear,
            subsidiary_id: props.selectedSubsidiary
          }
        })

        // Get cost and expense totals
        const costsResponse = await api.get(`/api/cost-submission/totals/${props.selectedSubsidiary}/${monthYear}01`)
        const costsData = costsResponse.data || { total_cost: 0, total_expenses: 0 }

        const monthName = new Date(selectedYear, month - 1).toLocaleString('default', { month: 'short' })
        
        fetchedData.push({
          month: monthName,
          total_revenue: parseFloat(revenueResponse.data.total_revenue || 0),
          total_costs: parseFloat(costsData.total_cost || 0),
          total_expenses: parseFloat(costsData.total_expenses || 0),
          gross_profit: parseFloat(revenueResponse.data.gross_profit || 0),
          ebitda: parseFloat(revenueResponse.data.ebitda || 0),
          profit_before_tax: parseFloat(revenueResponse.data.profit_before_tax || 0)
        })
      } catch (error) {
        const monthName = new Date(selectedYear, month - 1).toLocaleString('default', { month: 'short' })
        fetchedData.push({
          month: monthName,
          total_revenue: 0,
          total_costs: 0,
          total_expenses: 0,
          gross_profit: 0,
          ebitda: 0,
          profit_before_tax: 0
        })
      }
    }

    monthlyData.value = fetchedData
  } catch (error) {
    ElMessage.error('Failed to load graph data')
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  if (props.selectedSubsidiary) {
    await fetchGraphData()
  }
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  // Remove click listener from document
  document.removeEventListener('click', handleClickOutside)
})

// Add watch for selectedSubsidiary prop changes
watch(() => props.selectedSubsidiary, async (newValue) => {
  if (newValue) {
    await fetchGraphData()
  }
})

// Add watch for selectedYear prop changes
watch(() => props.selectedYear, async () => {
  if (props.selectedSubsidiary) {
    await fetchGraphData()
  }
})
</script>

<style scoped>
.finance-graphs {
  margin-top: 32px;
}

.graphs-filter {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subsidiary-selector {
  position: relative;
  min-width: 200px;
}

.selected-subsidiary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-subsidiary:hover {
  border-color: #cbd5e1;
}

.selected-subsidiary span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.selected-subsidiary i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-subsidiary i.rotate {
  transform: rotate(180deg);
}

.subsidiary-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.subsidiary-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

.graphs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.graph-container {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.graph-header {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.graph-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.graph {
  height: 300px;
}

.graphs-description {
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
}

.graphs-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.graphs-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.info-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
}

.info-tooltip i {
  font-size: 0.875rem;
  color: #94a3b8;
  transition: color 0.2s;
}

.info-tooltip:hover i {
  color: #0ea5e9;
}

.tooltip-content {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 200px;
}

.tooltip-formula {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-family: monospace;
  color: #94a3b8;
}

.info-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 12px;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner i {
  font-size: 2rem;
  color: #0ea5e9;
  animation: spin 1s linear infinite;
}

.loading-spinner p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.finance-graphs {
  position: relative;
}

@media (max-width: 1024px) {
  .graphs-grid {
    grid-template-columns: 1fr;
  }
}
</style> 